import { Component, ElementRef, OnInit, OnDestroy, ChangeDetectorRef, ViewChild } from '@angular/core';
import { ComponentBase } from 'common';
import { NetFacadeService } from 'netview';
import { NavigationService, NavLocation, NavType } from 'netview';
import { DiscResultsService } from 'discovery';
import { MenuItem } from 'primeng/api';
import { NetViewDto } from 'UvExplorerServer.Dtos.TS/NetViews';
import { AgentDto } from 'UvExplorerServer.Dtos.TS/Agents';
import { ScheduledDiscovery } from 'DiscoveryEngine.TS/Discovery';
import { ScheduledDiscoveryResultDto, SearchDeviceListDto } from 'UvExplorerServer.Dtos.TS/Discovery';
import { List } from 'DiscoveryEngine.TS/Utils';
import { Subscription } from 'rxjs/Rx';
import { ShareDto } from 'UvExplorerServer.Dtos.TS/Shares';
import { IdentityService } from 'identity';
import { DateService, MessageService } from 'base';
import { DeviceDto } from 'UvExplorerServer.Dtos.TS/Session';
import { DeviceSearchTableEntry } from '../device-search/device-search.component';
import { DeviceDetailsComponent, InfoSetsComponent } from 'infosets';
import { NgbOffcanvas } from '@ng-bootstrap/ng-bootstrap';

export enum SubNavType { None = 0, Agents = 1, Discoveries = 2, DiscResults = 3 };

export class SubNavItem {
	public guid: string;
	public label: string;
	public allowDrillDown: boolean;
	public hasMenu: boolean;
	public menuItems: MenuItem[];
	public tag: NetViewDto | AgentDto | ScheduledDiscovery | ScheduledDiscoveryResultDto;
}

export class SubNavContext {
	public type: SubNavType;
	public title: string;
	public allowCreation: boolean;
	public network: NetViewDto;
	public agent: AgentDto;
	public discovery: ScheduledDiscovery;
	public items: SubNavItem[];


	constructor() {
		this.clear();
	}

	public clear(): void {
		this.type = SubNavType.None;
		this.title = "";
		this.allowCreation = false;
		this.network = null;
		this.agent = null;
		this.discovery = null;
		this.items = [];
	}
}


class Share {
	public name: string;
	public description: string;
	public owner: string;
	public access: string;
	public network: NetViewDto;
	public share: ShareDto;

	constructor(s: ShareDto, n: NetViewDto) {
		this.name = n.Name;
		this.description = n.Description;
		this.owner = s.SourceUserName;
		this.access = this.capitalize(s.AccessType);
		this.network = n;
		this.share = s;
	}

	private capitalize(s: string): string {
		if (typeof s !== 'string') return ''
		return s.charAt(0).toUpperCase() + s.slice(1)
	}
}


@Component({
	selector: 'nav-tree',
	templateUrl: './navtree.component.html',
	styleUrls: ['./navtree.component.scss']
})
export class NavTreeComponent extends ComponentBase implements OnInit, OnDestroy {

	public readonly UseOffCanvas: boolean = false;

    @ViewChild("devicedetails", { static: true })
	public deviceDetails: DeviceDetailsComponent;

	@ViewChild("infosets", { static: false })
	public infoSets: InfoSetsComponent;

	@ViewChild('detailsOffCanvas') 
	public detailsOffCanvas: any;

	public networks: NetViewDto[];
	public subNavContext: SubNavContext = new SubNavContext();

	public shares: Share[];
	public shareSubNavContext: SubNavContext = new SubNavContext();

	private netViewItemsMap: { [index: string]: MenuItem[] } = {};
	private shareItemsMap: { [index: string]: MenuItem[] } = {};

	private netViewTreeLoadedSub: Subscription;
	private netViewTreeChangedSub: Subscription;
	private netViewTreeUnloadedSub: Subscription;
	private sharesLoadedSub: Subscription;
	private sharesChangedSub: Subscription;
	private sharesUnloadedSub: Subscription;

	// Boolean values tied to visibility of UI elements
	public displayNewNetworkDialog: boolean = false;
	public displayNewAgentDialog: boolean = false;
	public displayNewDiscoveryDialog: boolean = false;
	public displaySubNav: boolean = false;
	public displayDeleteNetViewConfirm: boolean = false;
	public displayDeleteAgentConfirm: boolean = false;
	public displayDeleteDiscoveryConfirm: boolean = false;
	public displayDeleteShareConfirm: boolean = false;
	public displaySourceSharingDialog: boolean = false;
	public displayTargetSharingDialog: boolean = false;

	public newItemName: string = "";
	public newItemDesc: string = "";

	public sourceSharedItemGuid: string = "";
	public targetSharedItemGuid: string = "";

	public filter = "";
	public searchText = "";
	public searchType = "contains";
	public searchResult: SearchDeviceListDto = null;
	public searchResultVisible: boolean = false;
	
	public selectedSearchDevice: DeviceDto = null;
	public get hasSelectedSearchDevice(): boolean {
		return !!this.selectedSearchDevice;
	}


	constructor(
		private netFacadeService: NetFacadeService,
		private navService: NavigationService,
		private discResultsService: DiscResultsService,
		private identityService: IdentityService,
		private dateService: DateService,
		private messageService: MessageService,
		private changeDetector: ChangeDetectorRef,
		private element: ElementRef<HTMLElement>,
		private offcanvasService: NgbOffcanvas) {

		super();

		this.rebuildNetworkList();
		this.rebuildShareList();

		this.netViewTreeLoadedSub = this.netFacadeService.onNetViewTreeLoaded.subscribe(() => {
			this.rebuildNetworkList();
		});
		this.netViewTreeChangedSub = this.netFacadeService.onNetViewTreeChanged.subscribe(() => {
			this.rebuildNetworkList();
		});
		this.netViewTreeUnloadedSub = this.netFacadeService.onNetViewTreeUnloaded.subscribe(() => {
			this.rebuildNetworkList();
		});
		this.sharesLoadedSub = this.netFacadeService.onSharesLoaded.subscribe(() => {
			this.rebuildShareList();
		});
		this.sharesChangedSub = this.netFacadeService.onSharesChanged.subscribe(() => {
			this.rebuildShareList();
		});
		this.sharesUnloadedSub = this.netFacadeService.onSharesUnloaded.subscribe(() => {
			this.rebuildShareList();
		});
	}

	public get hasNetworks(): boolean {
		return (!!this.networks && this.networks.length > 0);
	}

	public get hasShares(): boolean {
		return (!!this.shares && this.shares.length > 0);
	}

	public get isObserver(): boolean {
		return this.identityService.isObserver;
	}

	public get isAdmin(): boolean {
		return this.identityService.isAdmin;
	}

	get filteredNetworks() {
		return (!this.filter) ? this.networks : this.networks.filter(x => x.Name.toLowerCase().includes(this.filter.toLowerCase()));
	}

	get filteredShares() {
		return (!this.filter) ? this.shares : this.shares.filter(x => x.network.Name.toLowerCase().includes(this.filter.toLowerCase()));
	}

	public ngOnInit() {
		return;
	}

	public ngOnDestroy(): void {
		if (this.netViewTreeLoadedSub) {
			this.netViewTreeLoadedSub.unsubscribe();
			this.netViewTreeLoadedSub = undefined;
		}
		if (this.netViewTreeChangedSub) {
			this.netViewTreeChangedSub.unsubscribe();
			this.netViewTreeChangedSub = undefined;
		}
		if (this.netViewTreeUnloadedSub) {
			this.netViewTreeUnloadedSub.unsubscribe();
			this.netViewTreeUnloadedSub = undefined;
		}
		if (this.sharesLoadedSub) {
			this.sharesLoadedSub.unsubscribe();
			this.sharesLoadedSub = undefined;
		}
		if (this.sharesChangedSub) {
			this.sharesChangedSub.unsubscribe();
			this.sharesChangedSub = undefined;
		}
		if (this.sharesUnloadedSub) {
			this.sharesUnloadedSub.unsubscribe();
			this.sharesUnloadedSub = undefined;
		}
	}


	private static readonly TotalVh = 100;
	private static readonly TotalPx = 463;
	private static readonly RowHeight = 50;

	private get scrollHeights(): number[] {

		// totalScrollHeight = 100vh - 463

		let networksCount = 0;
		if (this.networks) {
			networksCount = this.networks.length;
		}

		let sharesCount = 0;
		if (this.shares) {
			sharesCount = this.shares.length;
		}

		let networksVh = 0;
		let networksPx = 0;
		let sharesVh = 0;
		let sharesPx = 0;

		if (networksCount > 0 && sharesCount > 0) {
			let networksWeight = (networksCount / (networksCount + sharesCount));

			networksVh = Math.round(networksWeight * NavTreeComponent.TotalVh);
			networksPx = Math.round(networksWeight * NavTreeComponent.TotalPx);

			sharesVh = (NavTreeComponent.TotalVh - networksVh);
			sharesPx = (NavTreeComponent.TotalPx - networksPx);
		}
		else if (networksCount > 0) {
			networksVh = NavTreeComponent.TotalVh;
			networksPx = NavTreeComponent.TotalPx;
		}
		else if (sharesCount > 0) {
			sharesVh = NavTreeComponent.TotalVh;
			sharesPx = NavTreeComponent.TotalPx;
		}

		return [networksCount, sharesCount, networksVh, networksPx, sharesVh, sharesPx];
	}

	public get myNetworksHeight(): string {

		let values: number[] = this.scrollHeights;

		let vh = values[2];
		let pixels = values[3];

		let networksCount = values[0];
		let desiredHeight = Math.ceil(networksCount * NavTreeComponent.RowHeight);

		let result = `min(calc(${vh}vh - ${pixels}px), ${desiredHeight}px)`;
		//console.log(result);
		return result;
	}

	public get sharedNetworksHeight(): string {

		let values: number[] = this.scrollHeights;

		let vh = values[4];
		let pixels = values[5];

		let sharesCount = values[1];
		let desiredHeight = Math.ceil(sharesCount * NavTreeComponent.RowHeight);

		let result = `min(calc(${vh}vh - ${pixels}px), ${desiredHeight}px)`;
		//console.log(result);
		return result;
	}

	public get isLicenseValid(): boolean {
		//return this.licensingService.isLicenseValid;
		return true;
	}

	private rebuildNetworkList(): void {
		this.networks = [...this.netFacadeService.networks];
		this.networks.sort((a, b) => a.Name.toLowerCase() > b.Name.toLowerCase() ? 1 : -1);

		const NetworkScrollPanelIndex = 0;
		const MaxHeight = 500;
		this.checkScrollPanelSize(NetworkScrollPanelIndex, MaxHeight);
	}

	private rebuildShareList(): void {
		this.shares = [];
		this.netFacadeService.targetShares.forEach((share: ShareDto) => {
			let network: NetViewDto = this.netFacadeService.getTargetNetworkByGuid(share.SharedItemGuid);
			if (!!network) {
				// Remove duplicate networks
				if (!this.shares.find((e: Share) => e.share.SharedItemGuid === share.SharedItemGuid)) {
					let entry = new Share(share, network);
					this.shares.push(entry);
				}
			}
		});
		this.shares.sort((a, b) => a.network.Name.toLowerCase() > b.network.Name.toLowerCase() ? 1 : -1);

		const ShareScrollPanelIndex = 1;
		const MaxHeight = 500;
		this.checkScrollPanelSize(ShareScrollPanelIndex, MaxHeight);
	}

	private checkScrollPanelSize(spIndex: number, maxHeight: number): void {
		if (this.element) {
			let scrollPanels = this.element.nativeElement.getElementsByTagName("p-scrollpanel");
			if (scrollPanels.length > spIndex) {
				let scrollPanel: Element = scrollPanels[spIndex];
				let scrollPanelRoot: Element = scrollPanel.children[0];
				let scrollPanelWrapper: Element = scrollPanelRoot.children[0];
				//let scrollPanelContent: Element = scrollPanelWrapper.children[0];

				(<any>scrollPanelRoot).style.height = '';

				setTimeout(() => {
					if (scrollPanelWrapper.clientHeight > maxHeight) {
						(<any>scrollPanelRoot).style.height = '' + maxHeight + 'px';
					}
				}, 0);
			}
		}
	}

	private enclose(method, args: any[]) {
		let self = this;
		return () => { method.apply(self, args) }
	}

	public handleDisplaySourceSharingDialog(netViewGuid: string): void {
		this.sourceSharedItemGuid = netViewGuid;
		this.displaySourceSharingDialog = true;
	}

	public handleDisplayTargetSharingDialog(shareGuid: string): void {
		let netViewGuid: string = "";
		this.shares.some((share: Share) => {
			if (share.share.Guid == shareGuid) {
				netViewGuid = share.network.Guid;
				return true;
			}
			else {
				return false;
			}
		});
		if (!!netViewGuid) {
			this.targetSharedItemGuid = netViewGuid;
			this.displayTargetSharingDialog = true;
		}
	}

	public netViewItems(guid: string): MenuItem[] {
		if (!this.netViewItemsMap[guid]) {
			this.netViewItemsMap[guid] = [
				{
					label: 'Delete', icon: 'pi pi-minus-circle', disabled: false, command: this.enclose(this.tryDeleteNetView, [guid])
				},
				{
					label: 'Sharing', icon: 'pi pi-share-alt', disabled: false, command: this.enclose(this.handleDisplaySourceSharingDialog, [guid])
				}
			];
		}
		return this.netViewItemsMap[guid];
	}

	public shareItems(guid: string): MenuItem[] {
		if (!this.shareItemsMap[guid]) {
			this.shareItemsMap[guid] = [
				{
					//label: 'Unshare', icon: 'pi pi-minus-circle', disabled: false, command: this.enclose(this.tryDeleteShare, [guid]),
					label: 'Sharing', icon: 'pi pi-share-alt', disabled: false, command: this.enclose(this.handleDisplayTargetSharingDialog, [guid])
				},
			];
		}
		return this.shareItemsMap[guid];
	}

	private netViewToDeleteGuid: string = "";

	public tryDeleteNetView(netViewGuid: string): void {
		this.netViewToDeleteGuid = netViewGuid;
		this.displayDeleteNetViewConfirm = true;
	}

	public cancelDeleteNetView(): void {
		this.netViewToDeleteGuid = "";
		this.displayDeleteNetViewConfirm = false;
	}

	public deleteNetView(): void {
		this.netFacadeService.deleteNetView(this.netViewToDeleteGuid);
		this.netViewToDeleteGuid = "";
		this.displayDeleteNetViewConfirm = false;
	}

	private agentToDeleteGuid: string = "";

	public tryDeleteAgent(agentGuid: string): void {
		this.agentToDeleteGuid = agentGuid;
		this.displayDeleteAgentConfirm = true;
	}

	public cancelDeleteAgent(): void {
		this.agentToDeleteGuid = "";
		this.displayDeleteAgentConfirm = false;
	}

	public deleteAgent(): void {
		this.netFacadeService.deleteAgent(this.agentToDeleteGuid).subscribe((result: boolean) => {
			this.subNavShowAgentsOf(this.subNavContext.network);
		});
		this.agentToDeleteGuid = "";
		this.displayDeleteAgentConfirm = false;
	}

	private discoveryToDeleteGuid: string = "";

	public tryDeleteDiscovery(discGuid: string): void {
		this.discoveryToDeleteGuid = discGuid;
		this.displayDeleteDiscoveryConfirm = true;
	}

	public cancelDeleteDiscovery(): void {
		this.discoveryToDeleteGuid = "";
		this.displayDeleteDiscoveryConfirm = false;
	}

	public deleteDiscovery(): void {
		let agent: AgentDto = this.subNavContext.agent;
		this.netFacadeService.deleteScheduledDiscovery(this.discoveryToDeleteGuid, agent).subscribe((result: boolean) => {
			this.subNavShowDiscoveriesOf(agent);
		});
		this.discoveryToDeleteGuid = "";
		this.displayDeleteDiscoveryConfirm = false;
	}

	//private shareToDeleteGuid: string = "";

	//public tryDeleteShare(shareGuid: string): void {
	//    this.shareToDeleteGuid = shareGuid;
	//    this.displayDeleteShareConfirm = true;
	//}

	//public cancelDeleteShare(): void {
	//    this.shareToDeleteGuid = "";
	//    this.displayDeleteShareConfirm = false;
	//}

	//public deleteShare(): void {
	//    this.netFacadeService.deleteTargetShare(this.shareToDeleteGuid);
	//    this.shareToDeleteGuid = "";
	//    this.displayDeleteShareConfirm = false;
	//}

	private clearNewItemProperties(): void {
		this.newItemName = "";
		this.newItemDesc = "";
	}

	public onSubNavNew(): void {
		this.clearNewItemProperties();

		switch (this.subNavContext.type) {
			case SubNavType.Agents:
				this.displayNewAgentDialog = true;
				break;
			case SubNavType.Discoveries:
				this.displayNewDiscoveryDialog = true;
				break;
			default:
				console.log("ERROR: Unable to show creation dialog. Unable to determine nav context type");
		}
	}

	public onNetworkClick(network: NetViewDto): void {
		let newNavLoc = new NavLocation(NavType.Network);
		newNavLoc.netViewGuid = network.Guid;
		this.navService.selectLocation(newNavLoc);
	}

	public onSubNavItemClick(item: SubNavItem): void {
		let newNavLoc = new NavLocation();

		switch (this.subNavContext.type) {
			case SubNavType.Agents:
				{
					let agent = <AgentDto>item.tag;
					let netView = this.netFacadeService.findAgentParent(agent.Guid);

					newNavLoc.type = NavType.Agent;
					newNavLoc.netViewGuid = netView.Guid;
					newNavLoc.agentGuid = agent.Guid;
				}
				break;
			case SubNavType.Discoveries:
				{
					let schedDisc = <ScheduledDiscovery>item.tag;
					let agent = this.netFacadeService.findSchedDiscParent(schedDisc.GUID);
					let netView = this.netFacadeService.findAgentParent(agent.Guid);

					newNavLoc.type = NavType.Discovery;
					newNavLoc.netViewGuid = netView.Guid;
					newNavLoc.agentGuid = agent.Guid;
					newNavLoc.schedDiscGuid = schedDisc.GUID;
				}
				break;
			case SubNavType.DiscResults:
				{
					let schedDiscRslt = <ScheduledDiscoveryResultDto>item.tag;
					let schedDiscGuid = schedDiscRslt.SchedDiscGuid;
					let agentGuid = schedDiscRslt.AgentGuid;
					let netView = this.netFacadeService.findAgentParent(schedDiscRslt.AgentGuid);

					newNavLoc.type = NavType.DiscResult;
					newNavLoc.netViewGuid = netView.Guid;
					newNavLoc.agentGuid = agentGuid;
					newNavLoc.schedDiscGuid = schedDiscGuid;
					newNavLoc.schedDiscRsltGuid = schedDiscRslt.Guid;
				}
				break;
		}
		this.navService.selectLocation(newNavLoc);

		this.closeDialogues();
	}

	public onSubNavDrillDown(item: SubNavItem): void {
		switch (this.subNavContext.type) {
			case SubNavType.Agents:
				// Do this on a timer to fix primeng crash
				setTimeout(() => {
					let agent = <AgentDto>item.tag;
					if (agent)
						this.subNavShowDiscoveriesOf(agent);
				}, 0);
				break;
			case SubNavType.Discoveries:
				// Do this on a timer to fix primeng crash
				setTimeout(() => {
					let disc = <ScheduledDiscovery>item.tag;
					if (disc)
						this.subNavShowDiscResultsOf(disc);
				}, 0);
				break;
		}
	}

	public showNewNetworkDialog(): void {
		this.clearNewItemProperties();
		this.displayNewNetworkDialog = true;
	}

	public onNewNetworkDialogClose(): void {
		setTimeout(() => {
			this.displayNewNetworkDialog = false;
		}, 0);
	}

	public onNewAgentDialogClose(): void {
		setTimeout(() => {
			this.displayNewAgentDialog = false;
		}, 0);
	}

	public onNewDiscoveryDialogClose(): void {
		setTimeout(() => {
			this.displayNewDiscoveryDialog = false;
		}, 0);
	}
	//subNav.style.left = showSubNavButton.getBoundingClientRect().right + 'px'; subNav.style.top = showSubNavButton.getBoundingClientRect().bottom + 'px'; 
	public showSubNav(network: NetViewDto, x: number, y: number): void {
		if (this.displaySubNav) {
			this.closeDialogues();
		}
		else {
			this.subNavShowAgentsOf(network);
			this.displaySubNav = true;
			this.changeDetector.detectChanges();
			//setTimeout(() => {
				let subNav = this.GetControlById("subnav");
				subNav.style.left = x + 'px';
				subNav.style.top = y + 'px';
			//}, 0);
		}
	}

	public closeDialogues() {
		this.displayNewNetworkDialog = false;
		this.displayNewAgentDialog = false;
		this.displayNewDiscoveryDialog = false;
		this.displaySubNav = false;
	}

	private subNavShowAgentsOf(network: NetViewDto): void {
		this.subNavContext.type = SubNavType.Agents;
		this.subNavContext.title = "Agents";
		this.subNavContext.allowCreation = true;
		this.subNavContext.network = network;
		this.subNavContext.agent = null;
		this.subNavContext.discovery = null;
		this.subNavContext.items = [];

		network.AgentGuids.ForEach((agentGuid: string) => {
			let agent = this.netFacadeService.findAgent(agentGuid);
			if (agent) {
				let subNavItem = new SubNavItem();
				subNavItem.guid = agentGuid;
				subNavItem.label = agent.Name;
				subNavItem.allowDrillDown = true;
				subNavItem.hasMenu = true;
				subNavItem.menuItems = [
					{
						label: 'Delete', icon: 'pi pi-minus-circle', disabled: false, command: this.enclose(this.tryDeleteAgent, [agentGuid])
					}
				];
				subNavItem.tag = agent;

				this.subNavContext.items.push(subNavItem);
			}
		}, this);

		this.subNavContext.items.sort((a, b) => a.label.toLowerCase() > b.label.toLowerCase() ? 1 : -1);
	}

	private subNavShowDiscoveriesOf(agent: AgentDto): void {
		this.subNavContext.type = SubNavType.Discoveries;
		this.subNavContext.title = "Discoveries";
		this.subNavContext.allowCreation = true;
		// retain existing this.subNavContext.network value
		this.subNavContext.agent = agent;
		this.subNavContext.discovery = null;
		this.subNavContext.items = [];

		agent.ScheduledDiscoveries.ForEach((disc: ScheduledDiscovery) => {
			let subNavItem = new SubNavItem();
			subNavItem.guid = disc.GUID;
			subNavItem.label = disc.Name;
			subNavItem.allowDrillDown = true;
			subNavItem.hasMenu = true;
			subNavItem.menuItems = [
				{
					label: 'Delete', icon: 'pi pi-minus-circle', disabled: false, command: this.enclose(this.tryDeleteDiscovery, [disc.GUID])
				}
			];
			subNavItem.tag = disc;

			this.subNavContext.items.push(subNavItem);
		}, this);

		this.subNavContext.items.sort((a, b) => a.label.toLowerCase() > b.label.toLowerCase() ? 1 : -1);
	}

	private subNavShowDiscResultsOf(disc: ScheduledDiscovery): void {
		this.subNavContext.type = SubNavType.DiscResults;
		this.subNavContext.title = "Discovery Runs";
		this.subNavContext.allowCreation = false;
		// retain existing this.subNavContext.network value
		// retain existing this.subNavContext.agent value
		this.subNavContext.discovery = disc;
		this.subNavContext.items = [];

		this.discResultsService.getSchedDiscResults(disc.GUID, "none").subscribe(
			(result: List<ScheduledDiscoveryResultDto>) => {
				result.ForEach((sdr: ScheduledDiscoveryResultDto) => {
					let subNavItem = new SubNavItem();
					subNavItem.guid = sdr.Guid;
					subNavItem.label = this.dateService.formatDateHm(sdr.EndTime);
					subNavItem.allowDrillDown = false;
					subNavItem.hasMenu = false;
					subNavItem.menuItems = [];
					subNavItem.tag = sdr;

					this.subNavContext.items.push(subNavItem);
				}, this);

				this.subNavContext.items.sort((a, b) => new Date(a.label) < new Date(b.label) ? 1 : -1);
			});
	}

	public onAgentCreated(): void {
		this.subNavShowAgentsOf(this.subNavContext.network);
	}

	public onDiscoveryCreated(): void {
		this.subNavShowDiscoveriesOf(this.subNavContext.agent);
	}

	public gotoConfiguration(): void {
		this.navService.gotoConfiguration();
	}

	public filterNetworks(): void {
		//console.log("Filter: ", this.filter);
	}

	public get enableSearchButton(): boolean {
		return !!this.searchText;
	}

	public searchDevices(event?: KeyboardEvent): void {
		if (!event || event.key === 'Enter') {
			if (this.enableSearchButton) {
				this.messageService.startWork();
				this.discResultsService.searchMasterDevices(this.searchText, this.searchType, 0, 1000).subscribe(
					(result: SearchDeviceListDto) => {
						this.messageService.endWork();
						console.log(result);
						this.searchResult = result;
						this.searchResultVisible = true;
					},
					(error) => {
						this.messageService.endWork();
						this.messageService.displayErrorMessage("Device search failed");
						this.searchResult = null;
					}
				);
			}
		}
	}

	public onSearchDeviceSelected(selectedEntry: DeviceSearchTableEntry): void {
		if (!!selectedEntry.guid) {

			let masterDeviceGuid = selectedEntry.guid;

			this.discResultsService.getMasterDeviceView(masterDeviceGuid).subscribe(
				(result: DeviceDto) => {
					this.selectedSearchDevice = result;

					if (this.UseOffCanvas) {
						this.openOffcanvas();
					}
					else {
						this.deviceDetails.show();
					}
					
					this.changeDetector.markForCheck();
				},
				(error?) => {
					this.messageService.displayErrorMessage("Could not retrieve the selected device");
				});
		}
		else {
			this.selectedSearchDevice = null;
		}
	}
	
	private openOffcanvas() {
		//console.log("openOffcanvas")
		//console.log(this.detailsOffCanvas)
		this.offcanvasService.open(this.detailsOffCanvas, { animation: false, ariaLabelledBy: 'offcanvas-basic-title', position:'bottom', keyboard: true  }).result.then(
			(result) => {
				//console.log("Offcanvas... event?")
			},
			(reason) => {
				//console.log("Closed offcanvas")
			},
		);
		//console.log("Off canvas should be open")
	}
}
